import FilterBottomSection from '@components/FilterComponents/FilterBottonSection';
import FilterTopSection from '@components/FilterComponents/FilterTopSection';
import { FavoriteButton } from '@components/MiscComponents/FavoriteButton';
import Modal from '@components/Modal';
import Portal from '@components/Modal/ModalPortal';
import { color, font, query as styleQuery } from '@design/templateFns';
import {
  AccessibilityFeatureEnum,
  LifestyleVerticalEnum,
  ListingFilters,
  ListingSortKeys,
  ParkingFeatureEnum,
  PetsAllowedEnum,
  PropertySubTypeEnum,
  PropertyTypeEnum,
  SortDir,
  StandardStatusEnum,
  ViewEnum,
} from '@engelvoelkersamericas/api';
import { useCurrency } from '@util/customHooks/useCurrency';
import { setStringArr } from '@util/filters';
import { LifestyleParam } from '@util/types';
import { Dayjs } from 'dayjs';
import isNumber from 'lodash/isNumber';
import { useTranslations } from 'next-intl';
import { useSearchParams } from 'next/navigation';
import { ChangeEvent, FC, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { AllFiltersSection, AllFiltersSectionProps } from '../FilterComponents/AllFiltersSection';
import FilterButton, { FilterButtonProps } from '../FilterComponents/FilterButton';
import { ButtonContainer } from './styles';
import {
  allLifestyleFilters,
  convertListingFiltersToQueryParams,
  getPropertySearchFilterData,
} from './utils';
import { ShopNavigationOptions } from '@data/shops';

export interface Lifestyle {
  water?: {
    primaryFilter?: {
      name?: string;
      value?: string;
    }[];
  };
  golf?: {
    primaryFilter?: {
      name?: string;
      value?: string;
    }[];
  };
  mountain?: {
    primaryFilter?: {
      name?: string;
      value?: string;
    }[];
  };
  wine?: {
    primaryFilter?: {
      name?: string;
      value?: string;
    }[];
  };
  recreationalRanch?: {
    primaryFilter?: {
      name?: string;
      value?: string;
    }[];
  };
  workingRanch?: {
    primaryFilter?: {
      name?: string;
      value?: string;
    }[];
  };
  ski?: {
    primaryFilter?: {
      name?: string;
      value?: string;
    }[];
  };
}

interface PropertySearchFiltersProps {
  currentFilters: ListingFilters;
  shopNavigationOptions: ShopNavigationOptions;
  hideRentFilters?: boolean;
  lifestyle?: LifestyleParam | null;
  onApply: (selectedFilters: ListingFilters) => void;
  onSavedSearchClick?: () => void;
  saveSearchStatus: {
    isEnable: boolean;
    isAlreadySaved: boolean;
  };
}

export type ViewMode = 'list' | 'map' | '50/50';
export type SortMode = 'listings' | 'priceAsc' | 'priceDesc' | 'onMarketDateAsc' | 'onMarketDateDesc';

export const EvaListSortKey = {
  'listings': ListingSortKeys.EvaId,
  'priceAsc': ListingSortKeys.UsdListPrice,
  'priceDesc': ListingSortKeys.UsdListPrice,
  'onMarketDateAsc': ListingSortKeys.OnMarketDate,
  'onMarketDateDesc': ListingSortKeys.OnMarketDate,
};

export interface ListingSearchParams {
  isBuy?: boolean;
  page?: number;
  sort?: SortMode;
  sortDir?: SortDir;
  view?: ViewMode;
  propertyTypes?: PropertySubTypeEnum[];
  listingStatus?: StandardStatusEnum[];
  minPrice?: number;
  maxPrice?: number;
}

// OTHER TYPES
export type SetArrType =
  | 'property_type'
  | 'listing_status'
  | 'listing_type'
  | 'type'
  | 'views'
  | 'amenities'
  | 'states'
  | 'provinces'
  | 'life_styles';

export type FieldType = 'price' | 'sq_feet' | 'lot_size' | 'stories';
export type NumberButtonType = 'vehicles' | 'bedrooms' | 'bathrooms';

// CONSTANTS - Price Slider, Sq Feet, Lot Size
const MAX_PRICE = 10000000;
const PRICE_INTERVAL = 10000;
const MAX_SQFT = 3000000;
const MAX_LOT_SIZE = 4000000;
const MAX_STORIES = 10;

const PropertySearchFilters: FC<PropertySearchFiltersProps> = ({
  currentFilters,
  hideRentFilters = false,
  lifestyle,
  onApply,
  onSavedSearchClick,
  saveSearchStatus,
}) => {
  const t = useTranslations();
  const { preferredCurrency, convertCurrency, formatCurrency } = useCurrency();
  const searchParams = useSearchParams();
  const dropdownRef = useRef<any | null>(null);
  const modal = useRef<any | null>(null);
  const [filters, setFilters] = useState<ListingFilters>(currentFilters);
  const [priceDisplay, setPriceDisplay] = useState<string | null>(null);
  const [selectedFilter, setSelectedFilter] = useState<string>('');
  const [selectedAmenities, setSelectedAmenities] = useState<string[]>([]);

  const openModalSection = () => {
    modal.current?.setOpen(true);
  };

  const closeAllModals = () => {
    modal.current?.toggle(false);
  };

  const convertPriceRangeByCurrency = async () => {
    const { minListPrice, maxListPrice } = filters;
    const currency = searchParams.get('currency') ?? preferredCurrency;
    if (!currency) {
      // Don't convert if currency is not set
      return { updatedMin: minListPrice, updatedMax: maxListPrice };
    }

    let updatedMin: number | undefined;
    if (isNumber(minListPrice) && !isNaN(minListPrice)) {
      updatedMin = convertCurrency(minListPrice, currency);
    }

    let updatedMax: number | undefined;
    if (isNumber(maxListPrice) && !isNaN(maxListPrice)) {
      updatedMax = convertCurrency(maxListPrice, currency);
    }

    return { updatedMin, updatedMax };
  };

  useEffect(() => {
    if (currentFilters) {
      setFilters(currentFilters);

      // Set selected amenities on load
      const queryParams = convertListingFiltersToQueryParams(currentFilters, lifestyle);
      const amenities = queryParams.get('selectedAmenities');
      if (amenities) {
        setSelectedAmenities(amenities.split(','));
      }
    }
  }, [currentFilters]);

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  //useEffect to handle the string of the min and max price to display, to change whenever the min and max price changes
  useEffect(() => {
    void (async () => {
      const { updatedMin, updatedMax } = await convertPriceRangeByCurrency();
      const currency = searchParams.get('currency') ?? preferredCurrency ?? 'USD';

      let title = '';
      if (updatedMin && !updatedMax) {
        title = `${currency} ${formatCurrency(updatedMin, currency)}+`;
      } else if (!updatedMin && updatedMax) {
        title = `${currency} 0 - ${formatCurrency(updatedMax, currency)}`;
      } else if (updatedMin && updatedMax) {
        title = `${currency} ${formatCurrency(updatedMin, currency)} - ${formatCurrency(
          updatedMax,
          currency,
        )}`;
      } else {
        title = t('Price');
      }
      setPriceDisplay(title);
    })();
  }, [filters.minListPrice, filters.maxListPrice, preferredCurrency]);

  useEffect(() => {
    void (async () => {
      const { updatedMin, updatedMax } = await convertPriceRangeByCurrency();
      if (updatedMin || updatedMax) {
        const newFilters: ListingFilters = {
          ...filters,
          minListPrice: updatedMin,
          maxListPrice: updatedMax,
        };
        setFilters(newFilters);
      }
    })();
  }, [preferredCurrency]);

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current?.contains(event.target)) {
      setSelectedFilter('');
    }
  };

  const handleSetSelectedFilter = (f: string) => {
    if (f === 'allFilters') {
      openModalSection();
      setSelectedFilter('');
    } else {
      if (f === selectedFilter) setSelectedFilter('');
      else setSelectedFilter(f);
    }
  };

  const handleSetArr = (type: string, b: string | string[]): void => {
    switch (type) {
      case 'property_type': {
        const newPropertyTypes = setStringArr(filters.propertySubTypes, b);
        setFilters({ ...filters, propertySubTypes: newPropertyTypes as PropertySubTypeEnum[] });
        break;
      }
      case 'listing_type': {
        const newListingTypes = setStringArr(filters.propertyTypes, b);
        setFilters({ ...filters, propertyTypes: newListingTypes as PropertyTypeEnum[] });
        break;
      }
      case 'listing_status': {
        const newListingStatuses = setStringArr(filters.listingStatuses, b);
        setFilters({ ...filters, listingStatuses: newListingStatuses as StandardStatusEnum[] });
        break;
      }
      case 'type': {
        const newParkingFeatures = setStringArr(filters.parkingFeatures, b);
        setFilters({ ...filters, parkingFeatures: newParkingFeatures as ParkingFeatureEnum[] });
        break;
      }
      case 'views': {
        const newViews = setStringArr(filters.views, b);
        setFilters({ ...filters, views: newViews as ViewEnum[] });
        break;
      }
      case 'amenities': {
        const newAmenities = setStringArr(selectedAmenities, b);
        setSelectedAmenities(newAmenities ?? []);
        setFilters((prev) => {
          const updated = { ...prev };

          if (!updated.amenities) {
            updated.amenities = {};
          }

          updated.amenities.hasPool = newAmenities ? newAmenities.includes('Pool') : false;
          updated.amenities.hasHeating = newAmenities ? newAmenities.includes('Heating') : false;
          updated.amenities.hasFireplace = newAmenities
            ? newAmenities.includes('Fireplace')
            : false;
          updated.amenities.hasBasement = newAmenities ? newAmenities.includes('Basement') : false;
          updated.amenities.hasHOA = newAmenities ? newAmenities.includes('HOA') : false;
          updated.amenities.hasCooling = newAmenities ? newAmenities.includes('A/C') : false;
          updated.petFriendlyOptions =
            newAmenities && newAmenities.includes('Pet Friendly')
              ? [PetsAllowedEnum.Yes]
              : undefined;
          updated.accessibilityFeatures =
            newAmenities && newAmenities.includes('Accessible')
              ? [
                  AccessibilityFeatureEnum.AccessibleEntrance,
                  AccessibilityFeatureEnum.AccessibleApproachWithRamp,
                  AccessibilityFeatureEnum.AccessibleDoors,
                  AccessibilityFeatureEnum.AccessibleFullBath,
                  AccessibilityFeatureEnum.AccessibleHallwayS,
                ]
              : undefined;

          return updated;
        });
        break;
      }
      case 'life_styles': {
        let newLifestyles = setStringArr(filters.lifestyles, b);
        if (!newLifestyles && lifestyle) {
          newLifestyles = allLifestyleFilters[lifestyle]?.map((l) => l.value) ?? [];
        }
        setFilters({ ...filters, lifestyles: newLifestyles as LifestyleVerticalEnum[] });
        break;
      }
      default:
        console.error('Property Search Filter: INVALID INPUT');
    }
  };

  const handleNumberButtonChange = (type: NumberButtonType, value: number | null) => {
    switch (type) {
      case 'vehicles':
        setFilters((prev) => ({ ...prev, minGarageSpaces: value ?? undefined }));
        break;
      case 'bedrooms':
        setFilters((prev) => ({ ...prev, minBedrooms: value ?? undefined }));
        break;
      case 'bathrooms':
        setFilters((prev) => ({ ...prev, minBathrooms: value ?? undefined }));
        break;
      default:
        console.error('Invalid entry.');
    }
  };

  const handleSliderChange = (event: Event, newValue: number | number[]) => {
    if (newValue === null) return;

    setFilters((prev) => ({ ...prev, minListPrice: newValue[0], maxListPrice: newValue[1] }));
  };

  const handleTextFieldChange = (
    e: ChangeEvent<HTMLInputElement>,
    field: FieldType,
    min?: 'min',
  ) => {
    const isFirst = min && min === 'min';
    switch (field) {
      case 'price': {
        const value = e.target.value ? parseInt(e.target.value) : null;
        if (isFirst) {
          setFilters((prev) => ({ ...prev, minListPrice: value ?? undefined }));
        } else {
          setFilters((prev) => ({ ...prev, maxListPrice: value ?? undefined }));
        }
        break;
      }
      case 'sq_feet':
        if (isFirst) {
          setFilters((prev) => ({ ...prev, minLivingArea: parseInt(e.target.value) }));
        } else {
          setFilters((prev) => ({ ...prev, maxLivingArea: parseInt(e.target.value) }));
        }
        break;
      case 'lot_size':
        if (isFirst) {
          setFilters((prev) => ({ ...prev, minLotSize: parseInt(e.target.value) }));
        } else {
          setFilters((prev) => ({ ...prev, maxLotSize: parseInt(e.target.value) }));
        }
        break;
      case 'stories':
        if (isFirst) {
          setFilters((prev) => ({ ...prev, minStories: parseInt(e.target.value) }));
        } else {
          setFilters((prev) => ({ ...prev, maxStories: parseInt(e.target.value) }));
        }
        break;
      default:
        console.log('Invalid entry.');
    }
  };

  const handleDateChange = (d: Dayjs | null, isFirst?: boolean): void => {
    if (isFirst) {
      if (d) {
        setFilters((prev) => ({ ...prev, minYearBuilt: d.year() }));
      } else {
        setFilters((prev) => ({ ...prev, minYearBuilt: undefined }));
      }
    } else {
      if (d) {
        setFilters((prev) => ({ ...prev, maxYearBuilt: d.year() }));
      } else {
        setFilters((prev) => ({ ...prev, maxYearBuilt: undefined }));
      }
    }
  };

  const onClickApply = () => {
    if (!isApplyDisable) {
      handleSetSelectedFilter('');
      onApply(filters);
      closeAllModals();
    }
  };

  const onClickClear = (type: string) => {
    let newFilters: ListingFilters = { ...filters };

    if (type === 'forSale') {
      delete newFilters.propertyTypes;
      delete newFilters.propertySubTypes;
    } else if (type === 'price') {
      delete newFilters.minListPrice;
      delete newFilters.maxListPrice;
    } else if (type === 'bedsAndBath') {
      delete newFilters.minBedrooms;
      delete newFilters.minBathrooms;
    } else if (type === 'allFilters') {
      newFilters = {};
    }

    setFilters(newFilters);
    onApply(newFilters);
    setSelectedFilter('');
    closeAllModals();
  };

  const onClickClose = (type: string) => {
    onClickClear(type);
    // removeFilterFromQueryParams(type);
  };

  const getFilterButtonData = (title: string) => {
    let isFilterApplied = false;
    if (title === 'Price' && priceDisplay) {
      isFilterApplied = !!priceDisplay && priceDisplay !== 'Price';
      title = priceDisplay;
    }

    if (title === 'Listing Type') {
      isFilterApplied = true;

      const numOfListingTypes = searchParams.get('listingTypes')
        ? searchParams.get('listingTypes')!.toString().split(',').length
        : 2;
      let numOfFilters = numOfListingTypes;

      const numOfPropertyTypes = searchParams.get('propertyTypes')
        ? searchParams.get('propertyTypes')!.toString().split(',').length
        : 0;
      numOfFilters += numOfPropertyTypes;

      title = t('Listing Type');
      if (numOfFilters > 0) {
        title += ` (${numOfFilters})`;
      }
    }

    if (
      title === 'Beds & Baths' &&
      (searchParams.get('bedrooms') || searchParams.get('bathrooms'))
    ) {
      isFilterApplied = true;
      const localeBd = t('Bd');
      const localeBa = t('Ba');
      const numBedrooms = searchParams.get('bedrooms');
      const numBathrooms = searchParams.get('bathrooms');

      if (numBedrooms && numBathrooms) {
        const bedrooms = numBedrooms.toString().replace('+', '');
        title = `${bedrooms} ${localeBd} & ${parseFloat(numBathrooms.toString())} ${localeBa}`;
      } else {
        if (numBedrooms) {
          const bedrooms = numBedrooms.toString().replace('+', '');
          title = `${bedrooms} ${localeBd}`;
        }
        if (numBathrooms) {
          const bathrooms = numBathrooms.toString().replace('+', '');
          title = `${bathrooms} ${localeBa}`;
        }
      }
    } else if (title === 'Beds & Baths') {
      title = t('Beds & Baths');
    }

    return { title, isFilterApplied };
  };

  const searchFilterData = getPropertySearchFilterData(hideRentFilters, lifestyle);

  const allFiltersProps: AllFiltersSectionProps = {
    maxAllowedPrice: MAX_PRICE,
    priceInterval: PRICE_INTERVAL,
    minPrice: filters.minListPrice,
    maxPrice: filters.maxListPrice,
    minSqft: filters.minLivingArea,
    maxSqft: filters.maxLivingArea,
    minLotSize: filters.minLotSize,
    maxLotSize: filters.maxLotSize,
    minYear: filters.minYearBuilt,
    maxYear: filters.maxYearBuilt,
    maxAllowedSqft: MAX_SQFT,
    maxAllowedLotSize: MAX_LOT_SIZE,
    maxAllowedStories: MAX_STORIES,
    searchFilterData,
    selectedListingTypes: filters.propertyTypes ?? [],
    selectedPropertyTypes: filters.propertySubTypes ?? [],
    selectedViews: filters.views ?? [],
    selectedListingStatus: filters.listingStatuses ?? [],
    selectedBedrooms: filters.minBedrooms,
    selectedBathrooms: filters.minBathrooms,
    selectedGarage: {
      type: filters.parkingFeatures,
      vehicles: filters.minGarageSpaces,
    },
    selectedAmenities,
    selectedLifeStyle: filters.lifestyles ?? [],
    minStories: filters.minStories,
    maxStories: filters.maxStories,
    handleSetSelectedFilter: handleSetSelectedFilter,
    handleSetArr,
    handleNumberButtonChange,
    handleSliderChange,
    handleTextFieldChange,
    handleDateChange,
    isDivider: true,
  };

  const filterButtonProps: Omit<FilterButtonProps, 'selected' | 'name' | 'title'> = {
    ...allFiltersProps,
    onClickApply,
    onClickClear,
    onClickClose,
    propertyTypes: searchFilterData.propertyTypes,
    listingTypes: searchFilterData.listingTypes,
    bedrooms: searchFilterData.bedrooms,
    bathrooms: searchFilterData.bathrooms,
    amenities: searchFilterData.amenities,
  };

  const isApplyDisable = useMemo(() => {
    const {
      minListPrice,
      maxListPrice,
      minLivingArea,
      maxLivingArea,
      minLotSize,
      maxLotSize,
      minStories,
      maxStories,
    } = filters;

    if ((minListPrice || minListPrice == 0) && maxListPrice && minListPrice >= maxListPrice) {
      console.log('Disable due to price');
      return true;
    }
    if ((minLivingArea || minLivingArea == 0) && maxLivingArea && minLivingArea >= maxLivingArea) {
      console.log('Disable due to sqft');
      return true;
    }
    if (minLotSize && maxLotSize && minLotSize >= maxLotSize) {
      console.log('Disable due to lot size');
      return true;
    }
    if (minStories && maxStories && minStories >= maxStories) {
      console.log('Disable due to stories');
      return true;
    }
    return false;
  }, [filters]);

  const filterButtonsCached = useMemo(() => {
    return searchFilterData.filterButtons.map((b) => {
      let item = getFilterButtonData(b.title);
      return { ...b, item };
    });
  }, [searchParams, priceDisplay]);

  return (
    <FilterContainer ref={dropdownRef}>
      <ButtonContainer>
        {filterButtonsCached &&
          filterButtonsCached.map((b) => {
            return (
              <FilterButton
                {...filterButtonProps}
                key={b.value}
                name={b.value}
                title={b.item.title}
                selected={b.value === selectedFilter}
                isFilterApplied={b.item.isFilterApplied}
                hideRentFilters={hideRentFilters}
              />
            );
          })}
        <FilterButton
          {...filterButtonProps}
          title={t('All Filters')}
          name={'allFilters'}
          selected={'allFilters' === selectedFilter}
          hideRentFilters={hideRentFilters}
        />
        <SaveFilter $isActive={saveSearchStatus.isEnable} onClick={onSavedSearchClick}>
          <FavoriteButton
            $isFavorite={saveSearchStatus.isAlreadySaved}
            onClick={() => {}}
            dataTestId="property-search-save-button"
          />
          <StyledSaveTitle>
            {saveSearchStatus.isAlreadySaved ? t('Saved') : t('Save Search')}
          </StyledSaveTitle>
        </SaveFilter>
      </ButtonContainer>

      <Portal>
        {/* // Portal will render at the bottom of the page, look at page.tsx for implementation */}
        <Modal
          top={<FilterTopSection heading={t('All Filters')} onClose={closeAllModals} />}
          center={<AllFiltersSection {...allFiltersProps} hideRentFilters={hideRentFilters} />}
          bottom={
            <FilterBottomSection
              isApplyDisable={isApplyDisable}
              onClickApply={onClickApply}
              onClear={() => onClickClear('allFilters')}
            />
          }
          ref={modal}
        />
      </Portal>
    </FilterContainer>
  );
};

export default PropertySearchFilters;

const FilterContainer = styled.div``;
const SaveFilter = styled.div<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 20px;
  color: ${color('gray4')};
  ${font('subhead-sm')}
  text-transform: capitalize;
  min-width: 119px;
  ${({ $isActive }) =>
    !$isActive &&
    `pointer-events: none;
    opacity: 0.5;`}

  @media only screen and (${styleQuery.atLeast('tablet')}) and (max-width: 1280px) {
    margin-left: auto;
  }
`;
const StyledSaveTitle = styled.div`
  cursor: pointer;
`;
